import moment from 'moment/moment';
import { TransportOrdersFilters } from './transportOrdersFilters';

export type TimePeriod = 'single-month' | 'quarter' | 'half-year' | 'year' | 'month-range';

export type StatsFilters = {
    timePeriodSelector: TimePeriod;
    singleMonthYear?: {
        month: number;
        year: number;
    };
    monthYearRange?: {
        fromMonth: number;
        fromYear: number;
        toMonth: number;
        toYear: number;
    };
    quarter?: {
        quarter: number;
        year: number;
    };
    halfYear?: {
        halfYear: number;
        year: number;
    };
    yearFilter?: {
        year: number;
    };
    creator?: {
        id: string;
    };
    contractor?: {
        id: string;
    }
};

export const buildTransportOrdersFilters = (statsFilters: StatsFilters): TransportOrdersFilters => {
    const { contractor, creator } = statsFilters;

    return {
        creator,
        contractor,
        orderMonthYearRange: buildOrderMonthYearRange(statsFilters),
    }
}

export const buildOrderMonthYearRange = (statsFilters: StatsFilters): ({ fromMonth: number, fromYear: number, toMonth: number, toYear: number } | undefined) => {
    const { timePeriodSelector, singleMonthYear, monthYearRange, quarter, halfYear, yearFilter } = statsFilters;
    if (timePeriodSelector === 'single-month') {
        return singleMonthYear ? {
            fromMonth: singleMonthYear.month,
            fromYear: singleMonthYear.year,
            toMonth: singleMonthYear.month,
            toYear: singleMonthYear.year,
        } : undefined;
    }

    if (timePeriodSelector === 'month-range') {
        return monthYearRange;
    }

    if (timePeriodSelector === 'quarter' && quarter) {
        // Calculate month range for the selected quarter
        const startMonth = (quarter.quarter - 1) * 3 + 1; // Q1 = 1, Q2 = 4, Q3 = 7, Q4 = 10
        const endMonth = startMonth + 2; // 3 months per quarter

        return {
            fromMonth: startMonth,
            fromYear: quarter.year,
            toMonth: endMonth,
            toYear: quarter.year,
        };
    }

    if (timePeriodSelector === 'half-year' && halfYear) {
        // Calculate month range for the selected half-year
        const startMonth = (halfYear.halfYear - 1) * 6 + 1; // 1st half = 1 (Jan), 2nd half = 7 (Jul)
        const endMonth = startMonth + 5; // 6 months per half-year

        return {
            fromMonth: startMonth,
            fromYear: halfYear.year,
            toMonth: endMonth,
            toYear: halfYear.year,
        };
    }

    if (timePeriodSelector === 'year' && yearFilter) {
        // Full year: Jan to Dec
        return {
            fromMonth: 1,
            fromYear: yearFilter.year,
            toMonth: 12,
            toYear: yearFilter.year,
        };
    }

    return undefined;
}


const now = moment();
const currentMonthNumber = now.month() + 1;
const currentYearNumber = now.year();
const currentQuarterNumber = Math.ceil(currentMonthNumber / 3);
const currentHalfYearNumber = Math.ceil(currentMonthNumber / 6);

export const TIME_PERIOD_OPTIONS: Record<TimePeriod, { label: string; defaultStats: DefaultTimePeriodStats }> = {
    'single-month': {
        label: 'Miesiąc',
        defaultStats: {
            timePeriodSelector: 'single-month',
            singleMonthYear: {
                month: currentMonthNumber,
                year: currentYearNumber,
            },
        },
    },
    'quarter': {
        label: 'Kwartał',
        defaultStats: { 
            timePeriodSelector: 'quarter',
            quarter: {
                quarter: currentQuarterNumber,
                year: currentYearNumber,
            },
        },
    },
    'half-year': {
        label: 'Półrocze',
        defaultStats: { 
            timePeriodSelector: 'half-year',
            halfYear: {
                halfYear: currentHalfYearNumber,
                year: currentYearNumber,
            },
        },
    },
    'year': {
        label: 'Rok',
        defaultStats: { 
            timePeriodSelector: 'year',
            yearFilter: {
                year: currentYearNumber,
            },
        },
    },
    'month-range': {
        label: 'Własny zakres',
        defaultStats: {
            timePeriodSelector: 'month-range',
            monthYearRange: {
                fromMonth: currentMonthNumber,
                fromYear: currentYearNumber,
                toMonth: currentMonthNumber,
                toYear: currentYearNumber,
            },
        },
    },
};

export type SingleMonthYearFilter = {
    timePeriodSelector: 'single-month';
    singleMonthYear: {
        month: number;
        year: number;
    };
}

export type MonthYearRangeFilter = {
    timePeriodSelector: 'month-range';
    monthYearRange: {
        fromMonth: number;
        fromYear: number;
        toMonth: number;
        toYear: number;
    };
}

export type QuarterFilter = {
    timePeriodSelector: 'quarter';
    quarter: {
        quarter: number;
        year: number;
    };
}

export type HalfYearFilter = {
    timePeriodSelector: 'half-year';
    halfYear: {
        halfYear: number; // 1 = Jan-Jun, 2 = Jul-Dec
        year: number;
    };
}

export type YearFilter = {
    timePeriodSelector: 'year';
    yearFilter: {
        year: number;
    };
}

type DefaultTimePeriodStats =
    SingleMonthYearFilter
    | MonthYearRangeFilter
    | QuarterFilter
    | HalfYearFilter
    | YearFilter;
